import { Component, OnInit, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DrawStyle, DrawStyleService, DrawStyleType } from '../../services/draw-style.service';

// Layer-basierter Style (für Zeichnungslayer)
export interface LayerStyle {
	strokeColor: string;
	strokeWidth: number;
	fillColor: string;
	fillOpacity: number;
}

// GPKG-Feature Attribute
export type GpkgFeatureAttributes = Record<string, any>;

@Component({
	selector: 'bp-draw-style-modal',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './draw-style-modal.component.html',
	styleUrls: ['./draw-style-modal.component.css'],
})
export class DrawStyleModalComponent implements OnInit {
	activeModal = inject(NgbActiveModal);
	styleService = inject(DrawStyleService);

	// Legacy: Für alte Verwendung (Line vs Polygon)
	@Input() styleType: DrawStyleType = 'polygon';

	// Neu: Layer-basierter Modus
	@Input() layerId: string | null = null;
	@Input() layerStyle: LayerStyle | null = null;

	// GPKG-Modus: Für GeoPackage-Features
	@Input() gpkgMode = false;
	@Input() gpkgAttributesOnly = false; // Nur Attribute anzeigen (Karten-Klick)
	@Input() gpkgLayerName: string | null = null;
	@Input() gpkgAttributes: GpkgFeatureAttributes | null = null;

	style!: DrawStyle;

	// Für Attribut-Tabelle: sortierte Keys
	attributeKeys: string[] = [];

	get colorPalette(): string[] {
		return this.styleService.colorPalette;
	}

	get strokeWidths(): number[] {
		return this.styleService.strokeWidths;
	}

	get fillOpacities(): number[] {
		return this.styleService.fillOpacities;
	}

	// Im Layer-Modus immer beide Optionen anzeigen
	get isLineStyle(): boolean {
		if (this.layerId || this.gpkgMode) return false; // Layer/GPKG-Modus: immer Flächen-Optionen zeigen
		return this.styleType === 'line';
	}

	get isLayerMode(): boolean {
		return this.layerId !== null;
	}

	get isGpkgMode(): boolean {
		return this.gpkgMode;
	}

	get isGpkgAttributesOnly(): boolean {
		return this.gpkgAttributesOnly;
	}

	get title(): string {
		if ((this.gpkgMode || this.gpkgAttributesOnly) && this.gpkgLayerName) return this.gpkgLayerName;
		if (this.layerId) return 'Zeichenstil';
		return this.styleType === 'line' ? 'Linienstil' : 'Polygonstil';
	}

	ngOnInit(): void {
		if (this.layerStyle) {
			// Layer-basierter Modus: Style aus Input verwenden
			this.style = { ...this.layerStyle };
		} else {
			// Legacy-Modus: Aus Service laden
			this.style = this.styleService.getStyle(this.styleType);
		}

		// GPKG-Attribute sortieren (wichtige zuerst)
		if (this.gpkgAttributes) {
			const keys = Object.keys(this.gpkgAttributes);
			// Wichtige Attribute nach vorne sortieren
			const priority = ['LBFO1', 'STGR1', 'SUBF1', 'SUB1', 'HUFO1', 'NAME', 'ID'];
			this.attributeKeys = keys.sort((a, b) => {
				const aUpper = a.toUpperCase();
				const bUpper = b.toUpperCase();
				const aPriority = priority.findIndex(p => aUpper.includes(p));
				const bPriority = priority.findIndex(p => bUpper.includes(p));
				if (aPriority >= 0 && bPriority >= 0) return aPriority - bPriority;
				if (aPriority >= 0) return -1;
				if (bPriority >= 0) return 1;
				return a.localeCompare(b);
			});
		}
	}

	onStrokeColorChange(color: string): void {
		this.style.strokeColor = color;
	}

	onFillColorChange(color: string): void {
		this.style.fillColor = color;
	}

	onStrokeWidthChange(width: number): void {
		this.style.strokeWidth = width;
	}

	onFillOpacityChange(opacity: number): void {
		this.style.fillOpacity = opacity;
	}

	onReset(): void {
		if (this.layerId) {
			// Layer-Modus: Standard-Stil
			this.style = {
				strokeColor: '#3388ff',
				strokeWidth: 3,
				fillColor: '#3388ff',
				fillOpacity: 0.2
			};
		} else {
			this.styleService.resetToDefault(this.styleType);
			this.style = this.styleService.getStyle(this.styleType);
		}
	}

	onSave(): void {
		if (this.layerId) {
			// Layer-Modus: Nur Style-Objekt zurückgeben
			this.activeModal.close({
				strokeColor: this.style.strokeColor,
				strokeWidth: this.style.strokeWidth,
				fillColor: this.style.fillColor,
				fillOpacity: this.style.fillOpacity
			});
		} else {
			// Legacy-Modus: Im Service speichern
			this.styleService.setStyle(this.style, this.styleType);
			this.activeModal.close(this.style);
		}
	}

	onCancel(): void {
		this.activeModal.dismiss();
	}

	formatOpacity(opacity: number): string {
		return `${Math.round(opacity * 100)}%`;
	}
}
